import {
  addDays,
  dayIn,
  instanceDate,
  periodStart,
  taskInstanceId,
  resubscribing,
  type CalendarEvent,
  type CalendarSource,
  type EventCheck,
  type Goal,
  type Household,
  type Idea,
  type Kudos,
  type ListItem,
  type Member,
  type Note,
  type Redemption,
  type Reward,
  type ShoppingList,
  type Streak,
  type Task,
  type TaskInstance,
  type ShiftPlan,
  type Timetable,
  type Todo,
  type Voucher,
  type Wish,
  type WishClaim,
  PARENT_SESSION_MS,
} from '@shh/shared';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { firebase } from '../lib/firebase';
import type { Session } from './session';

export type WithId<T> = T & { id: string };
/** A paired shared device (wall display); lastSeenAt since 1.29, older entries only have pairedAt. */
export type Device = { name: string; pairedAt: number; lastSeenAt?: number };

export interface FamilyData {
  /** All first snapshots arrived (cache or server). */
  loaded: boolean;
  householdId: string;
  role: 'parent' | 'teen' | 'kid' | 'device';
  /** The signed-in person (parents, teens with own login). Devices have none. */
  meId?: string;
  household?: WithId<Household>;
  timeZone: string;
  /** Today as YYYY-MM-DD in the household's time zone – changes at midnight. */
  today: string;
  members: WithId<Member>[];
  tasks: WithId<Task>[];
  /** Today's task states by task id. */
  instances: Record<string, WithId<TaskInstance>>;
  todos: WithId<Todo>[];
  events: WithId<CalendarEvent>[];
  rewards: WithId<Reward>[];
  redemptions: WithId<Redemption>[];
  kudos: WithId<Kudos>[];
  goal?: Goal;
  timetables: Record<string, Timetable>;
  /** Events ticked off today and yesterday (1.25), by `${eventId}_${date}`. */
  eventChecks: Record<string, WithId<EventCheck>>;
  /** Shift plans (1.25). */
  shiftPlans: WithId<ShiftPlan>[];
  /** Pinboard notes, newest first – the ones past their "bis" day are left out. */
  notes: WithId<Note>[];
  /** Streaks by `${memberId}_${taskId}` – written by the server, check `streakAlive` before showing. */
  streaks: Record<string, Streak>;
  /** Bring! shopping list, mirrored by the server; undefined = not connected. */
  shopping?: ShoppingList;
  /** The family's own shopping list (1.20) – used whenever Bring! is not connected. */
  listItems: WithId<ListItem>[];
  /** Freizeit (1.10): trip ideas, vouchers from the drawer, wish lists. */
  ideas: WithId<Idea>[];
  vouchers: WithId<Voucher>[];
  wishes: WithId<Wish>[];
  /** "Ich besorge das" by wish id – adults only (rules); empty for everybody else. */
  claims: Record<string, WishClaim>;
  /** Parents only (calendar links can be secret). */
  sources: WithId<CalendarSource>[];
  devices: WithId<Device>[];
  /** Wall: parent unlocked until (ms). */
  parentUntil: number;
}

const FamilyContext = createContext<FamilyData | null>(null);

export function useFamily(): FamilyData {
  const f = useContext(FamilyContext);
  if (!f) throw new Error('useFamily outside FamilyProvider');
  return f;
}

/**
 * The family data with events for a date range the live window does not cover (1.24): the
 * calendar browses months back, the window only reaches 14 days into the past. Ahead, the window
 * already holds everything (up to 800 events).
 */
export function useFamilyFor(from: string, to: string): FamilyData {
  const f = useFamily();
  const windowStart = addDays(f.today, -14);
  const needs = from < windowStart;
  const end = to < windowStart ? to : windowStart;
  const [extra, setExtra] = useState<WithId<CalendarEvent>[]>([]);
  const { db } = firebase();
  useEffect(() => {
    if (!needs) return;
    return watchQuery(
      query(
        collection(db, `households/${f.householdId}/events`),
        where('date', '>=', from),
        where('date', '<', end),
        orderBy('date'),
        limit(800),
      ),
      (s) => setExtra(s.docs.map((d) => withId<CalendarEvent>(d))),
    );
  }, [db, f.householdId, from, end, needs]);
  return useMemo(() => {
    if (!needs || !extra.length) return f;
    const known = new Set(f.events.map((e) => e.id));
    return { ...f, events: [...f.events, ...extra.filter((e) => !known.has(e.id))] };
  }, [f, extra, needs]);
}

const withId = <T,>(d: { id: string; data: () => DocumentData }) => ({
  ...(d.data() as T),
  id: d.id,
});

/** Recompute "today" every minute, so the wall rolls over to the new day by itself. */
function useToday(timeZone: string) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);
  // cheap; re-evaluated on every tick and when the household's time zone arrives
  return dayIn(timeZone);
}

/**
 * Listeners retry briefly on permission errors: right after the claims changed a listen may be
 * refused once (see resubscribing). After a deleted family the provider unmounts and they stop.
 */
const resilient = (subscribe: (onError: (e: FirestoreError) => void) => Unsubscribe) =>
  resubscribing(subscribe, {
    isTransient: (e) => e.code === 'permission-denied',
    // from the third try on, ask the server for a fresh token (claims may lag behind)
    beforeRetry: async (attempt) => firebase().auth.currentUser?.getIdToken(attempt > 2),
    onGiveUp: (e) => console.warn('Firestore listener stopped:', e.code),
  });
const watchQuery = (q: Query, next: (s: QuerySnapshot) => void) =>
  resilient((onError) => onSnapshot(q, next, onError));
const watchDoc = (ref: DocumentReference, next: (d: DocumentSnapshot) => void) =>
  resilient((onError) => onSnapshot(ref, next, onError));

type Ready = Extract<Session, { status: 'ready' }>;

export function FamilyProvider({ session, children }: { session: Ready; children: ReactNode }) {
  const { db } = firebase();
  const hid = session.householdId;
  const isParent = session.role === 'parent';
  const [household, setHousehold] = useState<WithId<Household>>();
  const timeZone = household?.timeZone ?? 'Europe/Berlin';
  const today = useToday(timeZone);

  const [members, setMembers] = useState<WithId<Member>[]>();
  const [tasks, setTasks] = useState<WithId<Task>[]>();
  /** today's task states by document id – mapped to tasks below (flexible ones by period) */
  const [instanceDocs, setInstanceDocs] = useState<Record<string, WithId<TaskInstance>>>();
  const [todos, setTodos] = useState<WithId<Todo>[]>();
  const [events, setEvents] = useState<WithId<CalendarEvent>[]>();
  const [series, setSeries] = useState<WithId<CalendarEvent>[]>([]);
  const [rewards, setRewards] = useState<WithId<Reward>[]>([]);
  const [redemptions, setRedemptions] = useState<WithId<Redemption>[]>([]);
  const [kudos, setKudos] = useState<WithId<Kudos>[]>([]);
  const [goal, setGoal] = useState<Goal>();
  const [timetables, setTimetables] = useState<Record<string, Timetable>>({});
  const [shiftPlans, setShiftPlans] = useState<WithId<ShiftPlan>[]>([]);
  const [eventChecks, setEventChecks] = useState<Record<string, WithId<EventCheck>>>({});
  const [streaks, setStreaks] = useState<Record<string, Streak>>({});
  const [notes, setNotes] = useState<WithId<Note>[]>([]);
  const [shopping, setShopping] = useState<ShoppingList>();
  const [listItems, setListItems] = useState<WithId<ListItem>[]>([]);
  const [ideas, setIdeas] = useState<WithId<Idea>[]>([]);
  const [vouchers, setVouchers] = useState<WithId<Voucher>[]>([]);
  const [wishes, setWishes] = useState<WithId<Wish>[]>([]);
  const [claims, setClaims] = useState<Record<string, WishClaim>>({});
  const [sources, setSources] = useState<WithId<CalendarSource>[]>([]);
  const [devices, setDevices] = useState<FamilyData['devices']>([]);
  const [parentUntil, setParentUntil] = useState(0);

  // Long-lived subscriptions (not day-dependent)
  useEffect(() => {
    const base = `households/${hid}`;
    const list = <T,>(q: Query, set: (v: WithId<T>[]) => void) =>
      watchQuery(q, (s) => set(s.docs.map((d) => withId<T>(d))));
    const unsubs = [
      watchDoc(doc(db, base), (d) => d.exists() && setHousehold(withId<Household>(d))),
      list<Member>(query(collection(db, `${base}/members`), orderBy('sortOrder')), setMembers),
      list<Task>(collection(db, `${base}/tasks`), setTasks),
      list<Todo>(collection(db, `${base}/todos`), setTodos),
      list<ShiftPlan>(collection(db, `${base}/shiftPlans`), setShiftPlans),
      list<Note>(
        query(collection(db, `${base}/notes`), orderBy('createdAt', 'desc'), limit(30)),
        setNotes,
      ),
      list<Reward>(collection(db, `${base}/rewards`), setRewards),
      list<Redemption>(
        query(collection(db, `${base}/redemptions`), where('status', '==', 'requested')),
        setRedemptions,
      ),
      list<Kudos>(
        query(collection(db, `${base}/kudos`), orderBy('createdAt', 'desc'), limit(12)),
        setKudos,
      ),
      watchDoc(doc(db, `${base}/goals/family`), (d) =>
        setGoal(d.exists() ? (d.data() as Goal) : undefined),
      ),
      watchDoc(doc(db, `${base}/shopping/bring`), (d) =>
        // connected = a list was chosen; a failed sync alone leaves only { error }
        setShopping(d.exists() && d.get('listUuid') ? (d.data() as ShoppingList) : undefined),
      ),
      list<ListItem>(collection(db, `${base}/shoppingItems`), setListItems),
      list<Idea>(collection(db, `${base}/ideas`), setIdeas),
      list<Voucher>(collection(db, `${base}/vouchers`), setVouchers),
      list<Wish>(collection(db, `${base}/wishes`), setWishes),
      watchQuery(collection(db, `${base}/streaks`), (s) =>
        setStreaks(Object.fromEntries(s.docs.map((d) => [d.id, d.data() as Streak]))),
      ),
      watchQuery(collection(db, `${base}/timetables`), (s) =>
        setTimetables(Object.fromEntries(s.docs.map((d) => [d.id, d.data() as Timetable]))),
      ),
    ];
    if (isParent) {
      unsubs.push(list<Device>(collection(db, `${base}/devices`), setDevices));
    }
    if (session.role === 'device') {
      // The server stamps the end with its own clock. A session that starts while we watch is
      // counted from now on this device instead – a wall clock that is off made the parent area
      // close at once (nightly E2E run, 07.10.). Only a session found on loading keeps the server
      // time (we cannot know how much of it is left).
      let seen: number | undefined;
      unsubs.push(
        watchDoc(doc(db, `${base}/parentSessions/${session.user.uid}`), (d) => {
          const server = d.exists() ? ((d.get('expiresAt') as number) ?? 0) : 0;
          if (server === seen) return;
          const fresh = seen !== undefined && server > 0;
          seen = server;
          setParentUntil(fresh ? Date.now() + PARENT_SESSION_MS : server);
        }),
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [db, hid, isParent, session.role, session.user.uid]);

  // "Ich besorge das": parents always, the wall while a parent PIN session exists – the rules
  // refuse everybody else, so nobody else even asks. (The pages show it only while the session
  // lasts; see useLens.)
  const adult = isParent || (session.role === 'device' && parentUntil > 0);
  useEffect(() => {
    if (!adult) return;
    return watchQuery(collection(db, `households/${hid}/wishClaims`), (s) =>
      setClaims(Object.fromEntries(s.docs.map((d) => [d.id, d.data() as WishClaim]))),
    );
  }, [db, hid, adult]);
  // Linked calendars: parents, and the wall while the parent PIN is open – it showed "Noch nichts
  // verbunden" in the settings there (Mike, 05.10.). The rules allow exactly these two.
  // (when the PIN session ends, the list is simply not handed out any more – see below)
  useEffect(() => {
    if (!adult) return;
    return watchQuery(collection(db, `households/${hid}/calendarSources`), (s) =>
      setSources(s.docs.map((d) => withId<CalendarSource>(d))),
    );
  }, [db, hid, adult]);

  // Day-dependent subscriptions
  useEffect(() => {
    const base = `households/${hid}`;
    const unsubs = [
      // today plus the first days of this week, weekend and month ("1× pro Woche" …)
      watchQuery(
        query(
          collection(db, `${base}/taskInstances`),
          where('date', 'in', [
            ...new Set([
              today,
              periodStart('week', today),
              periodStart('weekend', today),
              periodStart('month', today),
            ]),
          ]),
        ),
        (s) =>
          setInstanceDocs(Object.fromEntries(s.docs.map((d) => [d.id, withId<TaskInstance>(d)]))),
      ),
      watchQuery(
        query(
          collection(db, `${base}/events`),
          where('date', '>=', addDays(today, -14)),
          orderBy('date'),
          limit(800),
        ),
        (s) => setEvents(s.docs.map((d) => withId<CalendarEvent>(d))),
      ),
      // events ticked off (1.25) – stars only count for today and yesterday
      watchQuery(
        query(
          collection(db, `${base}/eventChecks`),
          where('date', 'in', [today, addDays(today, -1)]),
        ),
        (s) => setEventChecks(Object.fromEntries(s.docs.map((d) => [d.id, withId<EventCheck>(d)]))),
      ),
      // weekly series start in the past – load them regardless of the date window
      watchQuery(query(collection(db, `${base}/events`), where('recurring', '==', true)), (s) =>
        setSeries(s.docs.map((d) => withId<CalendarEvent>(d))),
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [db, hid, today]);

  // each task's state today: its own day, or its period for flexible tasks
  const instances = useMemo(() => {
    if (!instanceDocs || !tasks) return undefined;
    const out: Record<string, WithId<TaskInstance>> = {};
    for (const t of tasks) {
      const i = instanceDocs[taskInstanceId(t.id, instanceDate(t.repeat, today))];
      if (i) out[t.id] = i;
    }
    return out;
  }, [instanceDocs, tasks, today]);

  const value = useMemo<FamilyData>(
    () => ({
      loaded: Boolean(members && tasks && instances && todos && events),
      householdId: hid,
      role: session.role,
      meId: session.memberId,
      household,
      timeZone,
      today,
      members: (members ?? []).filter((m) => !m.archived),
      tasks: tasks ?? [],
      instances: instances ?? {},
      todos: todos ?? [],
      // a recent series is in both lists – keep one copy
      events: [...(events ?? []), ...series.filter((e) => !events?.some((x) => x.id === e.id))],
      rewards: rewards.filter((r) => r.active !== false),
      redemptions,
      kudos,
      goal,
      timetables,
      eventChecks,
      shiftPlans,
      ideas,
      vouchers,
      wishes,
      claims: adult ? claims : {},
      sources: adult ? sources : [],
      devices,
      parentUntil,
      streaks,
      shopping,
      listItems,
      notes: notes.filter((n) => !n.until || n.until >= today),
    }),
    [
      members,
      tasks,
      instances,
      todos,
      events,
      series,
      hid,
      session.role,
      session.memberId,
      household,
      timeZone,
      today,
      rewards,
      redemptions,
      kudos,
      goal,
      timetables,
      eventChecks,
      shiftPlans,
      ideas,
      vouchers,
      wishes,
      claims,
      adult,
      sources,
      devices,
      parentUntil,
      streaks,
      shopping,
      listItems,
      notes,
    ],
  );

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}
